import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, {
  BurstBackendPagination,
  getNormalizedLeaderboard,
  getNormalizedFrequency,
  getNormalizationStatus,
  NormalizedLeaderboardRow,
  NormalizedFrequencyPoint,
  DocCountStatus,
} from "../api/client";
import NgramFilterPanel, { FilterState } from "../components/NgramFilterPanel";
import FrequencyChart from "../components/FrequencyChart";

const DEFAULT_PAGE_SIZE = 50;

/* ── Leaderboard item type for this page ── */
type NormLeaderboardItem = {
  ngram_id: number;
  ngram: string;
  domain?: string;
  field?: string;
  subfield?: string;
  n_words: number;
  raw_score: number;
  normalized_score: number;
  avg_norm_freq: number | null;
  rank?: number;
};

const fmtNum = (n: number | null | undefined) =>
  typeof n === "number"
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(n)
    : "—";

/* ───────────────────────────────────────────────────────────── */

const NormalizedBurstPage: React.FC = () => {
  const { method } = useParams<{ method: "macd" | "kleinberg" }>();
  const currentMethod: "macd" | "kleinberg" = method === "macd" ? "macd" : "kleinberg";

  /* normalization status */
  const [normStatus, setNormStatus] = useState<DocCountStatus | null>(null);

  /* filters */
  const [filters, setFilters] = useState<FilterState>({
    domainId: null, fieldId: null, subfieldId: null,
    nWords: null, ngram: null,
    domainName: null, fieldName: null, subfieldName: null,
  });
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);

  /* table state */
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NormLeaderboardItem[]>([]);
  const [pagination, setPagination] = useState<BurstBackendPagination | null>(null);

  /* selected row + chart data */
  const [selectedRow, setSelectedRow] = useState<NormLeaderboardItem | null>(null);
  const [rawSeries, setRawSeries] = useState<{ date: string; count: number }[]>([]);
  const [normSeries, setNormSeries] = useState<{ date: string; count: number }[]>([]);
  const [chartError, setChartError] = useState<string | null>(null);

  /* ── Fetch normalization status on mount ── */
  useEffect(() => {
    getNormalizationStatus()
      .then(setNormStatus)
      .catch(() => setNormStatus({ has_data: false, total_records: 0, subfields_covered: 0, quarters_covered: 0, date_range: null }));
  }, []);

  /* ── Build query params ── */
  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      method: currentMethod,
      page,
      page_size: DEFAULT_PAGE_SIZE,
      sort_order: sortOrder,
    };
    if (filters.subfieldId) params.subfield_id = filters.subfieldId;
    else if (filters.fieldId) params.field_id = filters.fieldId;
    else if (filters.domainId) params.domain_id = filters.domainId;
    if (filters.nWords) params.n_words = filters.nWords;
    if (filters.ngram?.text) params.ngram_text = filters.ngram.text;
    if (start) params.start = start;
    if (end) params.end = end;
    return params;
  }, [currentMethod, filters, page, sortOrder, start, end]);

  /* ── Fetch leaderboard ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getNormalizedLeaderboard(queryParams)
      .then((res) => {
        if (cancelled) return;
        const mapped: NormLeaderboardItem[] = (res.data || []).map(
          (r: NormalizedLeaderboardRow, idx: number) => ({
            ngram_id: r.ngram_id,
            ngram: r.text,
            domain: r.domain,
            field: r.field,
            subfield: r.subfield,
            n_words: r.n_words,
            raw_score: r.raw_score,
            normalized_score: r.normalized_score,
            avg_norm_freq: r.avg_norm_freq,
            rank: (res.pagination.page - 1) * res.pagination.page_size + (idx + 1),
          })
        );
        setData(mapped);
        setPagination(res.pagination);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError("Failed to load normalized leaderboard.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [queryParams]);

  /* ── Select a row → load both raw + normalized frequency ── */
  const handleSelectRow = (row: NormLeaderboardItem) => {
    setSelectedRow(row);
    setChartError(null);
    setRawSeries([]);
    setNormSeries([]);

    // Raw frequency
    api
      .get<{ frequency_data: { date: string; count: number }[] }>(`/v1/ngram/${row.ngram_id}/frequency`)
      .then((r) => setRawSeries(r.data.frequency_data || []))
      .catch(() => {});

    // Normalized frequency
    getNormalizedFrequency(row.ngram_id)
      .then((res) => {
        setNormSeries(
          (res.frequency_data || []).map((p: NormalizedFrequencyPoint) => ({
            date: p.date,
            count: p.normalized_count,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
        setChartError("Could not load normalized frequency data.");
      });
  };

  /* ── Handlers ── */
  const handleFilterChange = (next: FilterState) => { setFilters(next); setPage(1); };
  const handleTimeChange = (s: string | null, e: string | null) => { setStart(s); setEnd(e); setPage(1); };

  /* ── Render ── */
  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Normalized Burst Detection</h1>
        <div className="inline-flex rounded-lg overflow-hidden border">
          <Link
            className={`px-3 py-1 text-sm ${currentMethod === "kleinberg" ? "bg-gray-900 text-white" : "bg-white text-gray-700"}`}
            to="/normalized/kleinberg"
          >
            Kleinberg
          </Link>
          <Link
            className={`px-3 py-1 text-sm ${currentMethod === "macd" ? "bg-gray-900 text-white" : "bg-white text-gray-700"}`}
            to="/normalized/macd"
          >
            MACD
          </Link>
        </div>
      </div>

      {/* Normalization status banner */}
      {normStatus && !normStatus.has_data && (
        <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-yellow-800">
          <strong>Document count data not yet loaded.</strong> Burst scores shown below use raw
          counts as a fallback. Run <code>python -m app.preprocessing.fetch_doc_counts</code> to
          populate document counts from OpenAlex, then refresh.
        </div>
      )}
      {normStatus && normStatus.has_data && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          Normalization active: <strong>{normStatus.subfields_covered}</strong> subfields,{" "}
          <strong>{normStatus.quarters_covered}</strong> quarters ({normStatus.date_range}).
          Scores reflect occurrences <strong>per 1,000 documents</strong>.
        </div>
      )}

      <NgramFilterPanel onFilterChange={handleFilterChange} showTimePicker onTimeChange={handleTimeChange} />

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow">
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-medium text-gray-700">
            Leaderboard &mdash; {currentMethod.toUpperCase()} (normalized by document count)
          </h2>
        </div>

        <div className="p-4">
          {/* Pagination controls */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              Method: <strong>{currentMethod.toUpperCase()}</strong>
              {start || end ? <> &bull; Window: <strong>{start ?? "start"} &rarr; {end ?? "end"}</strong></> : <> &bull; <strong>All-time</strong></>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!pagination || page <= 1}
                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Prev</button>
              <div className="text-sm">Page {pagination?.page ?? 1} / {pagination?.total_pages ?? 1}</div>
              <button onClick={() => setPage((p) => pagination ? Math.min(pagination.total_pages, p + 1) : p + 1)}
                disabled={!pagination || (pagination && page >= pagination.total_pages)}
                className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">Next</button>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

          {loading ? (
            <div className="text-sm text-gray-600">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="p-2">#</th>
                    <th className="p-2 cursor-pointer" onClick={() => setSortOrder(o => o === "asc" ? "desc" : "asc")}>
                      N-gram
                    </th>
                    <th className="p-2">Subfield</th>
                    <th className="p-2">Length</th>
                    <th className="p-2 cursor-pointer" onClick={() => setSortOrder(o => o === "asc" ? "desc" : "asc")}>
                      Norm. Score {sortOrder === "asc" ? "▲" : "▼"}
                    </th>
                    <th className="p-2">Raw Score</th>
                    <th className="p-2">Avg Norm Freq</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.ngram_id}
                      className={`border-b cursor-pointer transition-colors ${
                        selectedRow?.ngram_id === row.ngram_id ? "bg-blue-50" : "hover:bg-gray-50"
                      }`}
                      onClick={() => handleSelectRow(row)}
                    >
                      <td className="p-2 text-gray-500">{row.rank}</td>
                      <td className="p-2 font-medium">{row.ngram}</td>
                      <td className="p-2 text-gray-600 text-xs">{row.subfield}</td>
                      <td className="p-2 text-center">{row.n_words}</td>
                      <td className="p-2 font-semibold text-blue-700">{fmtNum(row.normalized_score)}</td>
                      <td className="p-2 text-gray-500">{fmtNum(row.raw_score)}</td>
                      <td className="p-2 text-gray-500">{fmtNum(row.avg_norm_freq)}</td>
                    </tr>
                  ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-gray-400">No results</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      {selectedRow && (
        <div className="space-y-4">
          {/* Raw frequency chart */}
          {rawSeries.length > 0 && (
            <FrequencyChart
              data={rawSeries}
              ngramText={selectedRow.ngram}
              showTooltip showAxes
              xLabel="Year" yLabel="Raw Document Frequency"
              subtitle={<>Raw document counts (not normalized)</>}
              height={240}
              intervalStart={start ?? null}
              intervalEnd={end ?? null}
            />
          )}

          {/* Normalized frequency chart */}
          {normSeries.length > 0 ? (
            <FrequencyChart
              data={normSeries}
              ngramText={`${selectedRow.ngram} (normalized)`}
              showTooltip showAxes
              xLabel="Year" yLabel="Per 1,000 Documents"
              subtitle={<>Occurrences per 1,000 documents in subfield</>}
              height={240}
              intervalStart={start ?? null}
              intervalEnd={end ?? null}
            />
          ) : chartError ? (
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-sm text-red-600">{chartError}</div>
            </div>
          ) : rawSeries.length > 0 ? (
            <div className="bg-white rounded-xl shadow p-4">
              <div className="text-sm text-gray-500">
                Normalized frequency data not available. Load document counts first.
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default NormalizedBurstPage;
